import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {sizeClassStore} from 'stores';
import {fixedAnnotationStore, FSMStore} from 'webgl/stores';
import {getAnnotationData__JSON} from 'services/getAnnotationData';
import {Object3D} from 'three/src/core/Object3D';
import {AnnotationData} from 'webgl/types/AnnotationData';
import {useHandleGotoState} from 'webgl/hooks';

import './AnnotationBase.scss';
import './AnnotationImageClosable.scss';
import {ReactComponent as CloseCircleIcon} from 'assets/icons/close-circle.svg';

function AnnotationImageClosableItem(props: { description: AnnotationData, object: Object3D }) {
  const sizeClass = sizeClassStore(state => state.sizeClass);
  const handleGotoState = useHandleGotoState(props.object);
  const isPointerDown = useRef<boolean>(false);
  const currentFSMState = FSMStore(state => state.currentFSMState);

  const selected = useMemo<boolean>(() => {
    const {tags} = props.object.userData;
    return currentFSMState === tags?.gotoState;
  }, [currentFSMState, props.object.userData]);

  const onPointerDown = useCallback(() => isPointerDown.current = true, []);
  const onPointerUp = useCallback(() => {
    if (!isPointerDown.current) return;
    handleGotoState();
    isPointerDown.current = false;
  }, [handleGotoState]);
  return (
    <div className={`annotation imageClosable ${sizeClass}`}>
      <div className={`image ${sizeClass}`}><img alt={'zoom schema'} src={props.description.image}/></div>
      <div className="annotation-close" onPointerDown={onPointerDown} onPointerUp={onPointerUp}><CloseCircleIcon/></div>
    </div>
  );
}

const AnnotationImageClosableDescription: React.FC = () => {
  const sizeClass = sizeClassStore(state => state.sizeClass);
  const fixedAnnotationObjects = fixedAnnotationStore(state => state.fixedAnnotationObjects);

  const [fixedAnnotationDescriptions, setFixedAnnotationDescriptions] =
    useState<Array<{ description: AnnotationData, object: Object3D }>>([]);

  useEffect(() => {
    setFixedAnnotationDescriptions([]); // reset list when objects change before waiting for backend response

    const promiseArray: Array<Promise<{ description: AnnotationData, object: Object3D }>> = [];
    fixedAnnotationObjects.forEach(obj => {
      promiseArray.push(new Promise<{ description: AnnotationData, object: Object3D }>((resolve, reject) => {
        getAnnotationData__JSON(obj)
          .then(res => {
            if (res !== undefined)
              resolve({description: res, object: obj});
          });
      }));
    });
    Promise.allSettled(promiseArray).then(promiseResults => {
      // @ts-ignore value is not recognized??
      const data = promiseResults.filter(res => res.status === 'fulfilled').map(res => res.value);
      setFixedAnnotationDescriptions(data);
    });
  }, [fixedAnnotationObjects, setFixedAnnotationDescriptions]);

  if (!fixedAnnotationDescriptions || fixedAnnotationDescriptions.length < 1) return null;

  return (
    <div className={`fixedImageClosableAnnotationsContainer ${sizeClass}`}>
      {fixedAnnotationDescriptions.map(({description, object}) =>
        <AnnotationImageClosableItem key={description.key} description={description} object={object}/>
      )}
    </div>
  );
};

export default AnnotationImageClosableDescription;
